import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  effect,
  input,
  output,
  signal,
  viewChild,
  ElementRef
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { switchMap } from "rxjs";
import { MatSlider, MatSliderThumb } from "@angular/material/slider";
import { DecimalPipe } from "@angular/common";
import { ReplacePipe } from "../../../pipes/replace.pipe";

@Component({
  selector: "app-slider",
  templateUrl: "./slider.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    MatSlider,
    MatSliderThumb,
    DecimalPipe,
    ReplacePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SliderComponent {
  min = input.required<number>();
  max = input.required<number>();
  enabled = input.required<boolean>();
  parentForm = input.required<FormGroup<any>>();
  formControlKey = input.required<string>();

  confirm$ = output<number>();

  sliderInput = viewChild<ElementRef<HTMLInputElement>>("sliderInput");

  readonly value = signal<number>(0);
  private readonly isDragging = signal(false);

  private readonly controlValue = toSignal<string>(
    toObservable(this.parentForm).pipe(
      switchMap((form) => form.get(this.formControlKey()).valueChanges)
    )
  );

  constructor() {
    effect(() => {
      const incoming = this.controlValue();
      if (!this.isDragging() && incoming !== null) {
        this.value.set(parseInt(incoming));
      }
    });
  }

  onRawInput(event: InputEvent) {
    const { value: newValue } =
      event.currentTarget as (typeof event)["currentTarget"] & {
        readonly value?: unknown;
      };
    this.control.setValue(Number(newValue));
  }

  onValueChange(event: number) {
    this.value.set(event);
    this.confirm$.emit(event);
    this.control.setValue(event);
  }

  onDragStart(): void {
    this.sliderInput().nativeElement.addEventListener("input", this.onRawInput);
    this.isDragging.set(true);
  }

  onDragEnd(): void {
    this.sliderInput().nativeElement.removeEventListener(
      "input",
      this.onRawInput
    );
    this.isDragging.set(false);
  }

  formatLabel(value: number): string {
    if (value >= 1000) {
      return Math.round(value / 1000) + "k" + (value >= 20000 ? "+" : "");
    }
    return value.toString();
  }

  get control() {
    return this.parentForm().get(this.formControlKey());
  }
}
